/* ─────────────────────────────────────────────────────────────────────
   BS · Product PDP behaviour
   ───────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* ── helpers ───────────────────────────────────────────────────── */
  const $  = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  function fmtMoney(cents) {
    // Shopify money_format isn't accessible from JS — replicate USD; sections
    // that need other currencies should swap window.Shopify.formatMoney.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat || '${{amount}}');
    }
    const v = (cents / 100);
    return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // "$1,234" / "$1,234.50" — same style as the Liquid card notes
  function fmtNote(cents) {
    const d = cents / 100;
    return '$' + (Number.isInteger(d)
      ? d.toLocaleString('en-US')
      : d.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }

  /* ── config-card quantities ────────────────────────────────────── */
  // The number on a card's − / + counter (1 when the card has no counter)
  function cardCount(card) {
    const v = card.querySelector('[data-config-qty-val]');
    return v ? Math.max(0, parseInt(v.textContent, 10) || 0) : 1;
  }
  // How many of the add-on go in the cart: the counter minus any already included
  // (e.g. counter = fermenters, the chiller's own pump is included → count − 1 pump kits)
  function cardQty(card) {
    const off = parseInt(card.dataset.configQtyOffset || '0', 10);
    return Math.max(0, cardCount(card) - off);
  }
  // Whether the bundled product (e.g. BFT-67) is part of the card at its current count
  function bundleOn(card) {
    if (!card.dataset.configBundleId) return false;
    const min = parseInt(card.dataset.configBundleMin || '0', 10);
    return !min || cardCount(card) >= min;
  }
  // What the card adds to the price: add-on × quantity, plus the bundled product once when it's on
  function cardTotal(card) {
    const p = parseInt(card.dataset.configAddonPrice || '0', 10);
    const b = parseInt(card.dataset.configBundlePrice || '0', 10);
    return p * cardQty(card) + (bundleOn(card) ? b : 0);
  }
  // Keep a card's counter note and bundle message in step with its count
  function refreshCardNotes(card) {
    const note = card.querySelector('[data-config-qty-note]');
    if (note && note.dataset.tpl) {
      note.textContent = note.dataset.tpl.replace(/\[extra\]/g, cardQty(card)).replace(/\[count\]/g, cardCount(card));
    }
    const on = bundleOn(card);
    $$('[data-config-bundle-note], [data-config-bundle-each]', card).forEach(el => { el.hidden = !on; });
  }
  function setCardQty(card, n) {
    const box = card.querySelector('[data-config-qty]');
    const v = card.querySelector('[data-config-qty-val]');
    if (!box || !v) return;
    const min = parseInt(box.dataset.min || '1', 10);
    const max = parseInt(box.dataset.max || '1', 10);
    v.textContent = String(Math.min(max, Math.max(min, n)));
  }
  // Companion cards (e.g. the BFT-67): not a choice, they show — already added — once the
  // chosen card's counter reaches their minimum. The grid re-flows to the tiles on show.
  // An "optional" companion stays on screen below its minimum and the shopper can tap it on or off.
  function syncCompanions() {
    $$('.bs-pdp-config').forEach(sec => {
      const comps = $$('[data-config-companion]', sec);
      if (!comps.length) return;
      const r = sec.querySelector('input[type=radio]:checked');
      const chosen = r && r.closest('[data-config-card]');
      const n = chosen ? cardCount(chosen) : 0;
      comps.forEach(c => {
        const forced = n >= parseInt(c.dataset.configCompanionMin || '0', 10);
        const optional = c.dataset.configCompanionOptional === '1';
        const on = forced || (optional && c.dataset.userOn === '1');
        c.dataset.forced = forced ? '1' : '0';
        c.dataset.included = on ? '1' : '0';
        c.hidden = !(forced || optional);
        c.classList.toggle('sel', on);
        if (optional) c.setAttribute('aria-pressed', on ? 'true' : 'false');
        const st = c.querySelector('[data-companion-status]');
        if (st) st.textContent = forced ? (c.dataset.noteForced || '') : (optional ? (on ? c.dataset.noteOn : c.dataset.noteOff) || '' : '');
      });
      const grid = sec.querySelector('[data-config-grid]');
      if (grid) {
        const cols = $$('[data-config-card]', grid).filter(c => !c.hidden).length;
        grid.className = grid.className.replace(/bs-config-cols-\d+/, 'bs-config-cols-' + cols);
      }
    });
  }
  function shownCompanions(sec) {
    return $$('[data-config-companion]', sec).filter(c => !c.hidden && c.dataset.included === '1');
  }

  // Fermenter count from the selected pump card; "one per fermenter" counters follow it
  // until the shopper changes them by hand.
  function syncPerFermenter() {
    let fermenters = 1;
    $$('.bs-pdp-config input[type=radio]:checked').forEach(r => {
      const card = r.closest('[data-config-card]');
      const role = card && card.dataset.configQtyRole;
      if (role === 'extra_pumps') fermenters = cardQty(card) + 1;
      if (role === 'all_pumps' || role === 'fermenters') fermenters = cardCount(card);
    });
    $$('[data-config-card][data-config-qty-role="per_fermenter"]').forEach(card => {
      if (card.dataset.qtyTouched !== '1') { setCardQty(card, fermenters); refreshCardNotes(card); }
    });
  }

  /* ── variant matching ──────────────────────────────────────────── */
  function getVariants() {
    const tag = $('[data-variants-json]');
    if (!tag) return [];
    try { return JSON.parse(tag.textContent); } catch (e) { return []; }
  }

  function getSelectedOptions(form) {
    const out = {};
    $$('input[type=radio]:checked', form).forEach(r => { out[r.name] = r.value; });
    return out;
  }

  function findVariant(variants, picked) {
    return variants.find(v => {
      return (v.options || []).every((val, i) => {
        const optName = v.option1 !== undefined && i === 0 ? null : null; // not needed; use product.options ordering via the form
        return Object.values(picked).includes(val);
      });
    }) || null;
  }

  // More reliable variant lookup: match by option-name → value pairs using
  // the option ordering Shopify gives us (option1/2/3).
  function lookupVariant(variants, productOptionNames, picked) {
    return variants.find(v => {
      for (let i = 0; i < productOptionNames.length; i++) {
        const want = picked[productOptionNames[i]];
        const got  = v['option' + (i + 1)];
        if (want != null && got !== want) return false;
      }
      return true;
    }) || null;
  }

  /* ── availability map: which option-values are reachable, given the
        current selection of OTHER option groups (this is what greys
        the 120V tile when 20G or 30G is selected). ───────────────── */
  function computeAvailability(variants, productOptionNames, picked) {
    // Returns: { [optionName]: { [value]: bool } }
    const result = {};
    productOptionNames.forEach((name) => { result[name] = {}; });

    variants.forEach(v => {
      if (!v.available) return;
      productOptionNames.forEach((name, i) => {
        const val = v['option' + (i + 1)];
        // Check whether all OTHER picked options match
        const otherMatch = productOptionNames.every((n2, j) => {
          if (j === i) return true;
          if (picked[n2] == null) return true;
          return picked[n2] === v['option' + (j + 1)];
        });
        if (otherMatch) result[name][val] = true;
      });
    });

    return result;
  }

  /* ── update UI for one PDP form ────────────────────────────────── */
  function bindPDP(root) {
    const form    = $('#bs-product-form', root) || $('form[id^="bs-product-form"]', root);
    if (!form) return;

    const variants = getVariants();
    if (!variants.length) return;

    // Pull product option names from the rendered fieldsets in order
    const fieldsets       = $$('fieldset.bs-var', form);
    const productOptions  = fieldsets.map(fs => {
      const input = fs.querySelector('input[type=radio]');
      return input ? input.name : null;
    }).filter(Boolean);

    const variantInput   = form.querySelector('input[name="id"]');
    const priceEl        = root.querySelector('[data-price]');
    const atcBtn         = form.querySelector('button[type="submit"]');
    const atcPriceEl     = root.querySelector('[data-atc-price]');
    const atcTextEl      = root.querySelector('[data-atc-text]');
    const installEl      = root.querySelector('[data-installments]');
    const addonCheckbox  = root.querySelector('[data-addon-checkbox]');
    const addonLabel     = root.querySelector('.bs-addon');
    const qtyInput       = root.querySelector('[data-qty-input]');
    const qtyUp          = root.querySelector('[data-qty-up]');
    const qtyDown        = root.querySelector('[data-qty-down]');

    function update() {
      const picked        = getSelectedOptions(form);
      const availability  = computeAvailability(variants, productOptions, picked);

      /* 1. Update each option tile's visual state */
      fieldsets.forEach(fs => {
        const optName = (fs.querySelector('input[type=radio]') || {}).name;
        if (!optName) return;
        $$('label.bs-opt', fs).forEach(lbl => {
          const inp = lbl.querySelector('input[type=radio]');
          if (!inp) return;
          const ok = !!(availability[optName] && availability[optName][inp.value]);
          lbl.classList.toggle('disabled', !ok);
          lbl.classList.toggle('sel', inp.checked && ok);
          inp.disabled = !ok;
        });

        // If current selection is now invalid, snap to first available
        const checked = fs.querySelector('input[type=radio]:checked');
        if (checked && !(availability[optName] && availability[optName][checked.value])) {
          const fallback = $$('input[type=radio]', fs).find(i => availability[optName][i.value]);
          if (fallback) {
            fallback.checked = true;
            // Re-run with new selection
            return update();
          }
        }

        // Update the inline "Step value" output
        const out = fs.querySelector('[data-out]');
        if (out && checked) {
          out.textContent = checked.value;
        }
      });

      /* 2. Toggle helper notes per group */
      $$('[data-note]', root).forEach(n => {
        const group   = n.dataset.note;
        // show only when this group has any disabled option-values
        const anyDisabled = $$('fieldset[data-group="' + group + '"] label.bs-opt.disabled', root).length > 0;
        n.style.display = anyDisabled ? 'flex' : 'none';
      });

      /* 3. Find selected variant */
      const picked2  = getSelectedOptions(form);
      const variant  = lookupVariant(variants, productOptions, picked2);

      if (variant) {
        if (variantInput) variantInput.value = variant.id;

        // Apply any variant-maps on config cards based on the chosen parent variant.
        // Each card with data-config-variant-map gets its addon-id (+ price, if known)
        // swapped to the entry whose pattern matches a substring of the parent variant title.
        try {
          const parentTitle = (variant.title || variant.public_title || '').toString();
          $$('[data-config-card][data-config-variant-map]').forEach(card => {
            const raw = card.getAttribute('data-config-variant-map') || '';
            // Parse "pattern:variantId[/priceCents];pattern:variantId[/priceCents];…"
            const pairs = raw.split(/[;\n]+/).map(s => s.trim()).filter(Boolean);
            let matched = null;
            for (const pair of pairs) {
              const [patRaw, valRaw] = pair.split(':').map(s => (s || '').trim());
              if (!patRaw || !valRaw) continue;
              // Substring match (case-insensitive) against the parent variant title
              if (parentTitle.toLowerCase().includes(patRaw.toLowerCase())) {
                // Value can be "12345" or "12345/19900" (id/cents) for price override
                const [idStr, priceStr] = valRaw.split('/').map(s => s.trim());
                const vid = parseInt(idStr.replace(/^variant=/, '').replace(/[^0-9]/g, ''), 10);
                if (vid) {
                  matched = { id: vid, price: priceStr ? parseInt(priceStr, 10) : null };
                  break;
                }
              }
            }
            // No entry for this size → this add-on doesn't exist for the chosen variant: hide the card
            // and make sure it can't stay selected. It comes back as soon as a matching size is picked.
            if (!matched) {
              card.dataset.configAddonId = '';
              card.dataset.configAddonPrice = '0';
              card.hidden = true;
              const r = card.querySelector('input[type=radio]');
              if (r && r.checked) {
                r.checked = false;
                card.classList.remove('sel');
                const first = card.closest('.bs-pdp-config') && card.closest('.bs-pdp-config').querySelector('[data-config-card]:not([hidden]) input[type=radio]');
                if (first) { first.checked = true; first.closest('[data-config-card]').classList.add('sel'); }
              }
            } else {
              card.hidden = false;
            }
            if (matched) {
              card.dataset.configAddonId = String(matched.id);
              // Real price of that variant in the visitor's market/currency (rendered by Liquid as
              // "id:cents;id:cents"). This wins over a price typed into the map, which can only be
              // right for one market.
              let livePrice = null;
              (card.getAttribute('data-config-variant-prices') || '').split(';').forEach(pair => {
                const [vidStr, centsStr] = pair.split(':');
                if (parseInt(vidStr, 10) === matched.id) livePrice = parseInt(centsStr, 10);
              });
              if (livePrice == null || isNaN(livePrice)) livePrice = matched.price;
              if (livePrice != null && !isNaN(livePrice)) {
                card.dataset.configAddonPrice = String(livePrice);
                const liveEl = card.querySelector('[data-config-price-live]');
                if (liveEl && livePrice > 0) {
                  const d = livePrice / 100;
                  liveEl.textContent = '+ $' + (Number.isInteger(d)
                    ? d.toLocaleString('en-US')
                    : d.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                }
              }
            }
          });
        } catch (_) {}

        let unitPrice = variant.price; // cents
        // add chiller addon if checked
        if (addonCheckbox && addonCheckbox.checked) {
          const addonPrice = parseInt(addonLabel.dataset.addonPrice || '0', 10);
          unitPrice += addonPrice;
        }
        // add any config-card addons (tier-style radio picker)
        try {
          $$('.bs-pdp-config').forEach(sec => {
            shownCompanions(sec).forEach(c => { unitPrice += parseInt(c.dataset.configAddonPrice || '0', 10); });
            const checked = sec.querySelector('input[type=radio]:checked');
            if (!checked) return;
            const card = checked.closest('[data-config-card]');
            if (!card) return;
            unitPrice += cardTotal(card);
          });
          // Cards with a counter or a bundled product show their own running total
          $$('[data-config-card]').forEach(card => {
            if (!card.querySelector('[data-config-qty-val]') && !card.dataset.configBundleId && !card.querySelector('[data-config-alt]')) return;
            const liveEl = card.querySelector('[data-config-price-live]');
            const t = cardTotal(card);
            if (liveEl && t > 0) liveEl.textContent = '+ ' + fmtNote(t);
          });
        } catch (_) {}
        const qty = Math.max(1, parseInt(qtyInput && qtyInput.value || '1', 10));
        const total = unitPrice * qty;

        if (priceEl)     priceEl.textContent     = fmtMoney(total);
        if (atcPriceEl)  atcPriceEl.textContent  = fmtMoney(total);
        // Installments text is Shopify's own widget now (payment_terms); nothing to rewrite here.

        /* Toggle DDP badge + price sub-line based on free-ship threshold; update dynamic gap. */
        try {
          const priceBlock = root.querySelector('.bs-price-block');
          const threshold  = priceBlock && parseInt(priceBlock.dataset.shipThreshold || '0', 10);
          if (priceBlock && threshold > 0) {
            const shipsFree = total >= threshold;
            const gapCents  = Math.max(0, threshold - total);
            const ddpFull = root.querySelector('[data-ddp-full]');
            const ddpLite = root.querySelector('[data-ddp-lite]');
            if (ddpFull) ddpFull.hidden = !shipsFree;
            if (ddpLite) ddpLite.hidden = shipsFree;
            /* Update every dynamic gap indicator — whole dollars, rounded up so the nudge never under-promises */
            const gapText = '$' + Math.ceil(gapCents / 100).toLocaleString('en-US');
            $$('[data-ship-gap]', root).forEach(el => { el.textContent = gapText; });
          }
        } catch (_) {}

        const mobileP = $('[data-mobile-price]');
        if (mobileP) mobileP.textContent = fmtMoney(total);

        if (atcBtn) {
          atcBtn.disabled = !variant.available;
          if (atcTextEl) {
            const fr = (document.documentElement.lang || '').slice(0, 2) === 'fr';
            atcTextEl.innerHTML = variant.available
              ? (fr ? 'Ajouter au panier' : 'Add to cart') + ' — <span data-atc-price>' + fmtMoney(total) + '</span>'
              : (fr ? 'Épuisé' : 'Sold out');
          }
        }
      } else if (atcBtn) {
        atcBtn.disabled = true;
        if (atcTextEl) atcTextEl.textContent = (document.documentElement.lang || '').slice(0, 2) === 'fr' ? 'Non offert' : 'Unavailable';
      }

      /* 4. Trigger specs section to switch tabs if a size is picked */
      const sizeName = productOptions.find(n => /size|capacity|volume/i.test(n));
      if (sizeName && picked2[sizeName]) {
        document.dispatchEvent(new CustomEvent('bs:size-change', { detail: { size: picked2[sizeName] } }));
      }
    }

    /* ── wire events ─────────────────────────────────────────────── */
    $$('input[type=radio]', form).forEach(i => i.addEventListener('change', update));

    if (addonLabel) {
      addonLabel.addEventListener('click', (e) => {
        // labels naturally toggle; sync visual class on next tick
        setTimeout(() => {
          addonLabel.classList.toggle('on', addonCheckbox.checked);
          update();
        }, 0);
      });
    }

    if (qtyUp) qtyUp.addEventListener('click', () => {
      qtyInput.value = Math.min(99, parseInt(qtyInput.value || '1', 10) + 1);
      update();
    });
    if (qtyDown) qtyDown.addEventListener('click', () => {
      qtyInput.value = Math.max(1, parseInt(qtyInput.value || '1', 10) - 1);
      update();
    });
    if (qtyInput) qtyInput.addEventListener('change', update);

    /* ── config-picker (tier radio cards in bs-product-config sections) ─ */
    function getConfigAddons() {
      // returns array of { id, price } for each .bs-pdp-config section's
      // currently-selected radio card that has an addon product attached.
      const out = [];
      $$('.bs-pdp-config').forEach(sec => {
        shownCompanions(sec).forEach(c => {
          const cid = parseInt(c.dataset.configAddonId || '0', 10);
          if (cid) out.push({ id: cid, price: parseInt(c.dataset.configAddonPrice || '0', 10), n: 1 });
        });
        const checked = sec.querySelector('input[type=radio]:checked');
        if (!checked) return;
        const card = checked.closest('[data-config-card]');
        if (!card) return;
        const id    = parseInt(card.dataset.configAddonId || '0', 10);
        const price = parseInt(card.dataset.configAddonPrice || '0', 10);
        const n     = cardQty(card);
        if (id && n > 0) out.push({ id, price, n });
        const bid   = parseInt(card.dataset.configBundleId || '0', 10);
        if (bid && bundleOn(card)) out.push({ id: bid, price: parseInt(card.dataset.configBundlePrice || '0', 10), n: 1 });
      });
      return out;
    }

    function syncSel(sec) {
      $$('[data-config-card]:not([data-config-companion])', sec).forEach(c => {
        const r = c.querySelector('input[type=radio]');
        c.classList.toggle('sel', !!(r && r.checked));
      });
    }

    // Sync .sel class on radio-card change + recompute buy-box price
    // Optional companion tiles (e.g. BFT-67 under 5 fermenters): tap or Enter/Space to add or remove
    function toggleCompanion(card) {
      if (card.dataset.configCompanionOptional !== '1' || card.dataset.forced === '1') return;
      card.dataset.userOn = card.dataset.userOn === '1' ? '0' : '1';
    }
    $$('.bs-pdp-config [data-config-companion-optional]').forEach(card => {
      card.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        card.click();
      });
    });

    $$('.bs-pdp-config [data-config-card]').forEach(card => {
      card.addEventListener('click', () => {
        const sec = card.closest('.bs-pdp-config');
        if (card.hasAttribute('data-config-companion')) toggleCompanion(card);
        setTimeout(() => {
          syncSel(sec);
          syncPerFermenter();
          syncCompanions();
          update();
        }, 0);
      });
    });

    // Tick-box upgrade on a card (e.g. add a solenoid valve): swap the add-on variant and pick that card
    $$('.bs-pdp-config [data-config-alt]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const card = btn.closest('[data-config-card]');
        if (!card) return;
        const on = btn.getAttribute('aria-pressed') !== 'true';
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        card.dataset.configAddonId = on ? btn.dataset.altId : btn.dataset.baseId;
        card.dataset.configAddonPrice = on ? btn.dataset.altPrice : btn.dataset.basePrice;
        $$('[data-alt-swap]', card).forEach(li => { li.textContent = on ? li.dataset.altSwap : li.dataset.baseText; });
        const each = card.querySelector('.bs-config-each');
        if (each && each.firstChild && each.firstChild.nodeType === 3) {
          each.firstChild.nodeValue = fmtNote(parseInt(card.dataset.configAddonPrice, 10)) + ' per ' + (each.dataset.unit || 'item');
        }
        const r = card.querySelector('input[type=radio]');
        if (r) r.checked = true;
        syncSel(card.closest('.bs-pdp-config'));
        syncPerFermenter();
        syncCompanions();
        update();
      });
    });

    // − / + counters on config cards: change the count and pick that card
    $$('.bs-pdp-config [data-config-qty-step]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const card = btn.closest('[data-config-card]');
        if (!card) return;
        setCardQty(card, cardCount(card) + parseInt(btn.dataset.configQtyStep, 10));
        refreshCardNotes(card);
        if (card.dataset.configQtyRole === 'per_fermenter') card.dataset.qtyTouched = '1';
        const r = card.querySelector('input[type=radio]');
        if (r) r.checked = true;
        syncSel(card.closest('.bs-pdp-config'));
        syncPerFermenter();
        syncCompanions();
        update();
      });
    });

    /* ── form submit: append chiller + any config addons to cart ────── */
    form.addEventListener('submit', async (e) => {
      const configAddons = getConfigAddons();
      const wantsChiller = addonCheckbox && addonCheckbox.checked;
      if (!wantsChiller && !configAddons.length) return; // default cart/add path

      e.preventDefault();
      const fd  = new FormData(form);
      const qty = parseInt(fd.get('quantity') || '1', 10);
      const items = [{
        id: parseInt(fd.get('id'), 10),
        quantity: qty
      }];
      if (wantsChiller) {
        const chillerId = parseInt(addonLabel.dataset.addonId, 10);
        if (chillerId) items.push({ id: chillerId, quantity: qty });
      }
      configAddons.forEach(a => items.push({ id: a.id, quantity: qty * (a.n || 1) }));

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error('cart add failed');
        const drawer = document.querySelector('cart-drawer, cart-notification');
        if (drawer && typeof drawer.open === 'function') {
          drawer.dispatchEvent(new CustomEvent('cart:refresh'));
          if (drawer.open) drawer.open();
        } else {
          window.location.href = '/cart';
        }
      } catch (err) {
        form.submit();
      }
    });

    // Initial sync
    syncPerFermenter();
    syncCompanions();
    update();
  }

  /* ── specs section: size tabs ──────────────────────────────────── */
  function bindSpecs(root) {
    const tabs = $$('.bs-specs-size', root);
    if (!tabs.length) return;
    function show(size) {
      tabs.forEach(b => b.classList.toggle('sel', b.dataset.s === size));
      $$('[data-specs-for]', root).forEach(g => {
        g.hidden = g.dataset.specsFor !== size;
      });
    }
    tabs.forEach(t => t.addEventListener('click', () => show(t.dataset.s)));
    document.addEventListener('bs:size-change', (e) => show(e.detail.size));
  }

  /* ── gallery: thumbs switch slides; videos play muted on a loop while on screen ── */
  function bindGallery(root) {
    const thumbs = $$('.bs-gal-thumb', root);
    const slides = $$('.bs-gal-slide', root);
    if (!slides.length) return;
    const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let onScreen = true;

    function sync() {
      slides.forEach(s => {
        const v = s.querySelector('video');
        if (!v) return;
        if (!s.hidden && onScreen && !still) {
          const p = v.play();
          if (p && p.catch) p.catch(() => { v.controls = true; });  // autoplay blocked: let them press play
        } else {
          v.pause();
        }
      });
    }

    slides.forEach(s => {
      const v = s.querySelector('video');
      if (v && still) v.controls = true;
    });

    thumbs.forEach(t => {
      t.addEventListener('click', () => {
        thumbs.forEach(x => x.classList.toggle('active', x === t));
        slides.forEach(s => {
          const on = s.dataset.mediaId === t.dataset.mediaId;
          s.hidden = !on;
          s.classList.toggle('is-active', on);
        });
        sync();
      });
    });

    const main = root.querySelector('.bs-gal-main');
    if (main && 'IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        onScreen = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.25 }).observe(main);
    } else {
      sync();
    }
  }

  /* ── phone sticky buy bar: price always in view; scrolls to options, or adds to cart once past the button ── */
  function bindSticky(root) {
    const bar = root.querySelector('[data-sticky-buy]');
    const atc = root.querySelector('.bs-atc-btn');
    if (!bar || !atc || atc.tagName !== 'BUTTON' || !('IntersectionObserver' in window)) return;
    const fr = (document.documentElement.lang || '').slice(0, 2) === 'fr';
    const btn = bar.querySelector('[data-sticky-btn]');
    const priceOut = bar.querySelector('[data-sticky-price]');
    const priceIn = root.querySelector('[data-price]');
    const target = root.querySelector('.bs-vars') || root.querySelector('.bs-buy');
    const mq = window.matchMedia('(max-width: 720px)');
    let atcVisible = true, past = false;
    if (document.querySelector('shopify-chat')) bar.classList.add('has-chat');

    function render() {
      const show = mq.matches && !atcVisible;
      bar.hidden = !show;
      document.body.classList.toggle('bs-sticky-on', show);
      if (!show) return;
      if (priceIn && priceOut) priceOut.textContent = priceIn.textContent;
      if (past) {
        btn.textContent = atc.disabled ? (fr ? 'Épuisé' : 'Sold out') : (fr ? 'Ajouter au panier' : 'Add to cart');
        btn.disabled = atc.disabled;
      } else {
        btn.textContent = fr ? 'Choisir les options' : 'Choose options';
        btn.disabled = false;
      }
    }

    new IntersectionObserver(([e]) => {
      atcVisible = e.isIntersecting;
      past = !e.isIntersecting && e.boundingClientRect.top < 0;
      render();
    }).observe(atc);

    btn.addEventListener('click', () => {
      if (past && !atc.disabled) atc.click();
      else if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    root.addEventListener('change', () => setTimeout(render, 0));
    if (mq.addEventListener) mq.addEventListener('change', render);
  }

  /* ── boot ──────────────────────────────────────────────────────── */
  function boot() {
    $$('.bs-pdp').forEach(bindGallery);
    $$('.bs-pdp').forEach(bindSticky);
    $$('.bs-pdp').forEach(bindPDP);
    $$('.bs-specs').forEach(bindSpecs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
